import * as React from "react";
import { formatNumber } from "@mandrac/shared";
import { Button, Checkbox, cn, Field, Input, Select, Textarea } from "@mandrac/ui";
import { useI18n } from "../lib/i18n";

/** Bare number input accepting Croatian decimal comma (table cells: label it with `aria-label`). `value` null = empty. */
export function NumberInput({
  value,
  onChange,
  integer,
  inputMode,
  ...props
}: Omit<React.ComponentProps<typeof Input>, "value" | "onChange" | "type"> & {
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  integer?: boolean;
}) {
  const { locale } = useI18n();
  const [text, setText] = React.useState(value === null || value === undefined ? "" : formatNumber(value, locale, 3));
  const last = React.useRef(value);
  React.useEffect(() => {
    if (value !== last.current) {
      setText(value === null || value === undefined ? "" : formatNumber(value, locale, 3));
      last.current = value;
    }
  }, [value, locale]);
  return (
    <Input
      {...props}
      inputMode={inputMode ?? (integer ? "numeric" : "decimal")}
      value={text}
      onChange={(e) => {
        const s = e.target.value;
        setText(s);
        const n = s.trim() === "" ? null : Number(s.replace(",", "."));
        const v = n === null || Number.isNaN(n) ? null : integer ? Math.round(n) : n;
        last.current = v;
        onChange(v);
      }}
    />
  );
}

/** Number input accepting Croatian decimal comma. `value` null = empty. */
export function NumberField({
  label,
  value,
  onChange,
  error,
  hint,
  min,
  step = "any",
  optional,
  integer,
  dock,
  className,
  id: _id,
  inputMode,
}: {
  label: React.ReactNode;
  value: number | null | undefined;
  onChange: (v: number | null) => void;
  error?: string;
  hint?: React.ReactNode;
  min?: number;
  step?: string;
  optional?: boolean;
  integer?: boolean;
  dock?: boolean;
  className?: string;
  id?: string;
  inputMode?: "decimal" | "numeric";
}) {
  const { t, te } = useI18n();
  return (
    <Field label={label} hint={hint} error={error ? te(error) : undefined} optionalLabel={optional ? t("common.optional") : undefined} className={className}>
      {(p) => <NumberInput {...p} dock={dock} inputMode={inputMode} integer={integer} value={value} min={min} step={step} onChange={onChange} />}
    </Field>
  );
}

export function TextField({
  label,
  value,
  onChange,
  error,
  hint,
  optional,
  type = "text",
  required,
  multiline,
  autoComplete,
  className,
  placeholder,
  inputMode,
}: {
  label: React.ReactNode;
  value: string | null | undefined;
  onChange: (v: string) => void;
  error?: string;
  hint?: React.ReactNode;
  optional?: boolean;
  type?: string;
  required?: boolean;
  multiline?: boolean;
  autoComplete?: string;
  className?: string;
  placeholder?: string;
  inputMode?: React.HTMLAttributes<HTMLInputElement>["inputMode"];
}) {
  const { t, te } = useI18n();
  return (
    <Field label={label} hint={hint} error={error ? te(error) : undefined} optionalLabel={optional ? t("common.optional") : undefined} className={className}>
      {(p) =>
        multiline ? (
          <Textarea {...p} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        ) : (
          <Input {...p} type={type} required={required} autoComplete={autoComplete} inputMode={inputMode} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} />
        )
      }
    </Field>
  );
}

export function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
  error,
  className,
  allowEmpty,
}: {
  label: React.ReactNode;
  value: T | null | undefined;
  onChange: (v: T) => void;
  options: Array<{ value: T; label: string }>;
  error?: string;
  className?: string;
  allowEmpty?: string;
}) {
  const { te } = useI18n();
  return (
    <Field label={label} error={error ? te(error) : undefined} className={className}>
      {(p) => (
        <Select {...p} value={value ?? ""} onChange={(e) => onChange(e.target.value as T)}>
          {allowEmpty !== undefined ? <option value="">{allowEmpty}</option> : null}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      )}
    </Field>
  );
}

export function CheckboxGroup<T extends string>({
  legend,
  options,
  value,
  onChange,
}: {
  legend: React.ReactNode;
  options: Array<{ value: T; label: string }>;
  value: T[];
  onChange: (v: T[]) => void;
}) {
  return (
    <fieldset className="m-0 flex min-w-0 flex-col gap-1 border-0 p-0">
      <legend className="mb-1 p-0 text-13 font-semibold text-muted">{legend}</legend>
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        {options.map((o) => (
          <Checkbox
            key={o.value}
            label={o.label}
            checked={value.includes(o.value)}
            onChange={(e) => onChange(e.target.checked ? [...value, o.value] : value.filter((x) => x !== o.value))}
          />
        ))}
      </div>
    </fieldset>
  );
}

/** Errors keyed by field path from an ApiError (`details[].path`). */
export function useFieldErrors() {
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  return { errors, setErrors, clear: () => setErrors({}) };
}

/** Password input with a show/hide button; `autoComplete` tells the browser whether to fill a saved password or offer a new one. */
export function PasswordField({
  label,
  value,
  onChange,
  error,
  hint,
  optional,
  required,
  autoComplete,
  className,
  tall,
}: {
  label: React.ReactNode;
  value: string;
  onChange: (v: string) => void;
  error?: string;
  hint?: React.ReactNode;
  optional?: boolean;
  required?: boolean;
  autoComplete: "current-password" | "new-password";
  className?: string;
  /** Controls at least 48 px tall (login and reset pages, used on phones). */
  tall?: boolean;
}) {
  const { t, te } = useI18n();
  const [shown, setShown] = React.useState(false);
  return (
    <Field label={label} hint={hint} error={error ? te(error) : undefined} optionalLabel={optional ? t("common.optional") : undefined} className={className}>
      {(p) => (
        <div className="flex gap-2">
          <Input {...p} type={shown ? "text" : "password"} required={required} autoComplete={autoComplete} autoCapitalize="none" spellCheck={false} className={tall ? "h-12" : undefined} value={value} onChange={(e) => onChange(e.target.value)} />
          <Button aria-pressed={shown} aria-label={shown ? t("login.hidePassword") : t("login.showPassword")} onClick={() => setShown((s) => !s)} className={cn("shrink-0 px-3", tall ? "h-12 min-w-12" : "")}>
            <EyeIcon off={shown} />
          </Button>
        </div>
      )}
    </Field>
  );
}

function EyeIcon({ off }: { off: boolean }) {
  return (
    <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7Z" />
      <circle cx="12" cy="12" r="3" />
      {off ? <path d="M4 4l16 16" /> : null}
    </svg>
  );
}
